import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/LanguageContext";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { tenantDisplayName } from "./tenantDisplay";
import { SEARCH_MAX_LENGTH, normalizeSearch } from "./useDebouncedSearch";

/**
 * Searchable practice picker for the super-admin plane (docs/auth-and-admin/
 * super-admin-lists-contract.md section 2).
 *
 * It replaces every `<select>` that was filled from the full tenants table. The list is
 * never downloaded: each open and each (debounced) keystroke asks
 * `GET /super-admin/tenants/options?q=&limit=` for `{ total, items: [{ tenant_id, slug, name }] }`,
 * so the cost is one small page however many practices exist.
 *
 * It is a WAI-ARIA 1.2 combobox with a listbox popup: the text input is the only focus stop
 * and points at the highlighted option through `aria-activedescendant`. Nothing in the popup
 * takes focus (a press on it is cancelled at mousedown, which keeps focus on the input without
 * stopping a scrollbar drag, and the listbox opts out of Chromium's focusable-scroller Tab stop),
 * so Tab and Shift+Tab leave the field like any other and the blur closes the popup. The input
 * holds the search text and nothing else (empty while the popup is closed); the current
 * selection is a label drawn over the empty input and exposed through `aria-describedby`, open
 * or closed.
 *
 * Contract with the caller
 *   - `value` is a `tenant_id`, `""` meaning "no practice chosen". The caller owns it.
 *   - `onChange(tenantId, option)` fires when the choice CHANGES; `option` is the
 *     `{ tenant_id, slug, name }` row, or `null` for the "all practices" choice. Choosing the
 *     current value again only closes the popup, as a native `<select>` does.
 *   - `fitWidth` makes the popup exactly as wide as the field (default: at least as wide, and
 *     up to 18rem so names stay readable); the sidebar switcher sets it, because a popup wider
 *     than its sidebar clips and adds a horizontal scrollbar to it.
 *   - `allowAll` adds an "all practices" entry that selects `""` (a filter); without it the
 *     picker is a pure chooser (the sidebar switcher).
 *
 * The behaviours that make this more than a plain autocomplete:
 *   - The selection is always visible. Closed, it is the label (with the full name as a
 *     tooltip). Open, it is the checked row of the listbox; when the result page does not hold
 *     it (the operator searched for something else) it is pinned under "all practices" as a
 *     "current" row. Chosen and seen rows are remembered by id, and only that memory is read to
 *     label `value`.
 *   - The default highlight follows what the operator is doing. With text in the box it is the
 *     first RESULT row, and only once the answer for exactly that text has landed: before that
 *     nothing is highlighted, so Enter after typing a name never picks "all practices" or a row
 *     of the previous text. With an empty box it is the current selection ("all practices"
 *     when nothing is chosen). Arrow keys and the pointer override it until the next answer.
 *   - A response for an older query never overwrites a newer one (a ticket per request), and
 *     while a newer query is still pending the result rows on screen are marked stale and
 *     cannot be chosen with Enter. The static rows ("all practices", the pinned current row)
 *     depend on no result, so an explicit arrow to them works before the first answer and
 *     after a failed read (which also shows a Retry).
 */

const SEARCH_DEBOUNCE_MS = 250;
const RESULT_LIMIT = 20;
const ALL = "__all__";

// A keydown that belongs to an input-method composition (moving between candidates, committing
// one, cancelling) is the IME's, not the listbox's. Chromium and Firefox flag it with
// `isComposing`; Safari sends the committing Enter after compositionend, marked only by keyCode 229.
const isImeKey = (e) => e.nativeEvent.isComposing || e.keyCode === 229;

const FIELD_CLASS =
  "w-full bg-white border border-slate-200 rounded-md pl-3 pr-8 py-2 text-sm text-slate-700 placeholder:text-slate-500 " +
  "focus:outline-none focus:ring-2 focus:ring-amber-300/40";

export default function TenantPicker({
  value,
  onChange,
  allowAll = false,
  allLabel,
  placeholder,
  ariaLabel,
  placement = "bottom",
  fitWidth = false,
  className = "",
  testId = "tenant-picker",
}) {
  const t = useT("superadmin");
  const listboxId = useId();
  const selectionId = `${listboxId}-selection`;
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const known = useRef(new Map()); // tenant_id -> { tenant_id, slug, name }, every row ever seen
  const ticket = useRef(0); // monotonically increasing request id; only the latest may land
  const typed = useRef(false); // true once the operator typed since the popup opened

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(""); // the text in the box, as typed
  const [result, setResult] = useState(null); // { query, total, items } | null while nothing has loaded
  const [failed, setFailed] = useState(false);
  // `/tenants/options` is super admin only: a platform operator with another role is refused,
  // which is a fact about the role, not an outage, so it is said once and not retried.
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(false);
  // The row key the operator highlighted with the keyboard or pointer; null = the default.
  const [pickedKey, setPickedKey] = useState(null);
  const [attempt, setAttempt] = useState(0); // bumped by Retry to re-ask the same query

  const allText = allLabel || t("tp.all");
  // What is sent, and what an answer must match to count as current.
  const searchText = normalizeSearch(query);
  const optionId = (index) => `${listboxId}-opt-${index}`;

  useEffect(() => {
    if (!open) return undefined;
    const id = ++ticket.current;
    const run = () => {
      setLoading(true);
      api.get("/super-admin/tenants/options", { params: { q: searchText, limit: RESULT_LIMIT } })
        .then((r) => {
          if (id !== ticket.current) return;
          const data = r.data;
          // A 200 that is not the page (the production proxy answers 200 + index.html for an
          // unknown /api path) is a failed read, not "no practice matches".
          if (!data || !Array.isArray(data.items) || !Number.isFinite(data.total)) {
            throw new Error("tenant options payload");
          }
          data.items.forEach((o) => known.current.set(o.tenant_id, o));
          setResult({ query: searchText, total: data.total, items: data.items });
          setFailed(false);
          setForbidden(false);
          setPickedKey(null); // a new answer brings its own default highlight
        })
        .catch((error) => {
          if (id !== ticket.current) return;
          setFailed(true);
          setForbidden(error?.response?.status === 403);
          setResult(null);
          setPickedKey(null);
        })
        .finally(() => { if (id === ticket.current) setLoading(false); });
    };
    const timer = setTimeout(run, typed.current ? SEARCH_DEBOUNCE_MS : 0);
    return () => {
      clearTimeout(timer);
      // Invalidate whatever this effect started: the query moved on or the popup closed.
      ticket.current += 1;
    };
  }, [open, searchText, attempt]);

  // The result rows answer the text in the box only once a response for exactly that text landed.
  const settled = Boolean(result) && result.query === searchText;
  const items = result?.items || [];

  const selected = value ? known.current.get(value) : null;
  const closedText = value ? (selected ? tenantDisplayName(selected) : value) : (allowAll ? allText : "");

  // The popup rows: the optional "all" entry, the current selection when the result page does
  // not hold it, then what the server returned. A row's key is its tenant_id (ALL for "all"),
  // so the pinned row and the same practice's result row are one highlight target.
  const rows = [];
  if (allowAll) rows.push({ key: ALL, option: null, pinned: false });
  if (value && !items.some((o) => o.tenant_id === value)) {
    rows.push({ key: value, option: selected || { tenant_id: value }, pinned: true });
  }
  items.forEach((o) => rows.push({ key: o.tenant_id, option: o, pinned: false }));
  const isStatic = (row) => row.key === ALL || row.pinned;

  const defaultKey = searchText
    ? (settled && items.length ? items[0].tenant_id : null)
    : (value || (allowAll ? ALL : (settled && items.length ? items[0].tenant_id : null)));
  const activeKey = [pickedKey, defaultKey].find((k) => k !== null && rows.some((r) => r.key === k)) ?? null;
  const active = activeKey === null ? -1 : rows.findIndex((r) => r.key === activeKey);

  const openPopup = () => {
    if (open) return;
    typed.current = false;
    setQuery("");
    setResult(null);
    setFailed(false);
    setPickedKey(null);
    setOpen(true);
  };
  const closePopup = useCallback(() => setOpen(false), []);

  const choose = (row) => {
    setOpen(false);
    const id = row.key === ALL ? "" : row.key;
    if (id === (value || "")) return; // the current choice: nothing changes
    if (row.option) known.current.set(row.option.tenant_id, row.option);
    onChange(id, row.option);
  };

  const onKeyDown = (e) => {
    if (isImeKey(e)) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { openPopup(); return; }
      if (rows.length === 0) return;
      const down = e.key === "ArrowDown";
      const next = active === -1
        ? (down ? 0 : rows.length - 1)
        : (active + (down ? 1 : -1) + rows.length) % rows.length;
      setPickedKey(rows[next].key);
      // The keyboard moved the highlight, so bring it into the listbox's viewport (the rows are
      // already rendered; only the highlight class changes). This scroll slides other rows under
      // a resting pointer, and Chromium answers with mouseover/mouseenter on the row now under it;
      // that is why the pointer takes the highlight on mousemove only (see the options below).
      document.getElementById(optionId(next))?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter" && open) {
      e.preventDefault();
      // A result row of a previous query is not what the operator is looking for; the static
      // rows depend on no result and stay choosable.
      const row = rows[active];
      if (row && (settled || isStatic(row))) choose(row);
    }
  };

  // Escape is handled on the whole widget, so it also works from the Retry button.
  const onRootKeyDown = (e) => {
    if (e.key === "Escape" && open && !isImeKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      closePopup();
      inputRef.current?.focus();
    }
  };

  // What is typed, pasted or composed becomes the query.
  const takeText = (text) => { openPopup(); typed.current = true; setPickedKey(null); setQuery(text); };

  // Focus leaving the whole widget (Tab, Shift+Tab, a click elsewhere) closes the popup.
  const onBlur = (e) => {
    if (!rootRef.current?.contains(e.relatedTarget)) closePopup();
  };

  const activeId = open && active !== -1 ? optionId(active) : undefined;
  const shownCount = items.length;

  return (
    <div ref={rootRef} className={`relative ${className}`} onBlur={onBlur} onKeyDown={onRootKeyDown} data-testid={testId}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        // The listbox is only rendered while the popup is open.
        aria-controls={open ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        aria-label={ariaLabel || t("tp.label")}
        autoComplete="off"
        maxLength={SEARCH_MAX_LENGTH}
        // The input only ever holds the search text (empty while closed), so whatever is typed,
        // pasted or composed is the query as entered. The selection is shown by the label below.
        value={open ? query : ""}
        aria-describedby={closedText ? selectionId : undefined}
        placeholder={open ? t("tp.searchPh") : (closedText ? "" : (placeholder || t("tp.placeholder")))}
        onFocus={openPopup}
        onClick={openPopup}
        onChange={(e) => takeText(e.target.value)}
        // Not every browser follows the commit of a composition with an input event, so the
        // committed text is read again when the composition ends.
        onCompositionEnd={(e) => takeText(e.currentTarget.value)}
        onKeyDown={onKeyDown}
        className={FIELD_CLASS}
        data-testid={`${testId}-input`}
      />
      {closedText && (
        // Closed: the visible label, hoverable so a truncated name can be read in full, and a
        // press on it opens the field like a press on the input. Open: visually hidden (the
        // input shows the search text) but still the input's description.
        <span
          id={selectionId}
          title={open ? undefined : closedText}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { inputRef.current?.focus(); openPopup(); }}
          className={open
            ? "sr-only"
            : "absolute inset-y-0 left-3 right-8 flex items-center text-sm text-slate-700 cursor-text"}
          data-testid={`${testId}-selection`}
        >
          <span className="truncate">{closedText}</span>
        </span>
      )}
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400">
        {loading && open ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronDown className="w-4 h-4" />}
      </span>

      {open && (
        // A press anywhere in the popup (an option, the list's scrollbar, a status line, Retry)
        // would move focus off the input and blur-close the popup before its click lands.
        // Cancelling mousedown keeps focus where it is; it does not cancel the click, nor a
        // scrollbar drag.
        <div
          className={`absolute z-30 left-0 min-w-full ${fitWidth ? "w-full" : "w-72"} rounded-md border border-slate-200 bg-white shadow-lg ${placement === "top" ? "bottom-full mb-1" : "top-full mt-1"}`}
          data-testid={`${testId}-popup`}
          aria-busy={!settled}
          onMouseDown={(e) => e.preventDefault()}
        >
          <ul
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel || t("tp.label")}
            // An overflowing scroller with no focusable child is a Tab stop in Chromium; the
            // keyboard reaches these options through the input, so the list is not one.
            tabIndex={-1}
            className="max-h-64 overflow-y-auto py-1"
          >
            {rows.map((row, index) => {
              const isSelected = row.key === ALL ? !value : row.key === value;
              const name = row.option ? tenantDisplayName(row.option) : allText;
              const stale = !settled && !isStatic(row);
              return (
                <li
                  key={row.key}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  title={name}
                  onClick={() => choose(row)}
                  // Only a real pointer movement moves the highlight. A row can also come to be
                  // under a still pointer because the list scrolled (an arrow key, the wheel):
                  // that fires mouseenter but no mousemove, and must not undo the keyboard's
                  // choice. A pointer movement never scrolls: the row is already under it.
                  onMouseMove={() => setPickedKey(row.key)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${index === active ? "bg-slate-100" : ""} ${stale ? "opacity-60" : ""} ${row.pinned ? "border-b border-slate-100" : ""}`}
                  data-testid={row.option ? `${testId}-option-${row.key}` : `${testId}-option-all`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-slate-800">{name}</span>
                    {row.option?.slug && <span className="block truncate text-xs text-slate-500" title={`/${row.option.slug}`}>/{row.option.slug}</span>}
                  </span>
                  {row.pinned && (
                    <span className="shrink-0 text-[10px] uppercase tracking-wider text-slate-400" data-testid={`${testId}-current-tag`}>
                      {t("tp.current")}
                    </span>
                  )}
                  {isSelected && <Check className="w-4 h-4 shrink-0 text-amber-600" />}
                </li>
              );
            })}
          </ul>
          {/* Status line outside the listbox: a listbox may only contain options. */}
          {failed && forbidden && (
            <p role="alert" className="px-3 py-2 text-xs text-slate-500" data-testid={`${testId}-forbidden`}>{t("tp.forbidden")}</p>
          )}
          {failed && !forbidden && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-rose-600">
              <p role="alert" className="flex-1" data-testid={`${testId}-error`}>{t("tp.error")}</p>
              <button
                type="button"
                onClick={() => { typed.current = false; setAttempt((n) => n + 1); }}
                className="shrink-0 rounded border border-rose-200 px-2 py-0.5 text-rose-700 hover:bg-rose-50"
                data-testid={`${testId}-retry`}
              >
                {t("tp.retry")}
              </button>
            </div>
          )}
          {!failed && !settled && <p className="px-3 py-2 text-xs text-slate-500" data-testid={`${testId}-loading`}>{t("tp.searching")}</p>}
          {!failed && settled && items.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-500" data-testid={`${testId}-empty`}>{t("tp.empty")}</p>
          )}
          {!failed && settled && result.total > shownCount && (
            <p className="px-3 py-2 text-xs text-slate-500 border-t border-slate-100" data-testid={`${testId}-more`}>
              {t("tp.more", { shown: shownCount, total: result.total })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
